import { useMemo, useState } from 'react';
import styled from 'styled-components';
import { BaseWorkTaskTypeEnum, ParentRelationshipType, WorkTaskDTO2 } from '../../api/api';
import Button from '../../components/button/Button';
import { useNavigateToTask } from '../../hooks/useNavigateToTask';
import useWorkTaskLock from '../../hooks/useWorkTaskLock';
import { CreateRelationOptions } from '../../models/TaskRelations';
import { SectionHeader } from '../../styling/FormStyling';
import CreateRelationDialog, { CreateRelationSide } from './CreateRelationDialog';
import RelationNode from './RelationNode';
import { buildRelationTasks, RelationSide } from './relationsGraph';
import { buildRelationsLayout } from './relationsLayout';

interface Props {
  task?: WorkTaskDTO2;
  onCreateRelatedTask?: (options: CreateRelationOptions) => void;
}

const RELATION_TYPE_BY_SIDE: Record<RelationSide, ParentRelationshipType> = {
  preparation: ParentRelationshipType.PreparationForParent,
  visit: ParentRelationshipType.FollowUpSameWorkOrderAsParent,
  followUp: ParentRelationshipType.FollowUpToParent
};

const connectorLabelWidth = (...lines: (string | undefined)[]) =>
  Math.max(...lines.map((line) => (line?.length ?? 0) * 6.2)) + 16;

const TaskRelationsDiagram = (props: Props) => {
  const { task, onCreateRelatedTask } = props;

  const [isCreateMode, setIsCreateMode] = useState(false);
  const [pendingSide, setPendingSide] = useState<CreateRelationSide>();

  const navigateToTask = useNavigateToTask();
  const { releaseWorkTaskLock } = useWorkTaskLock();

  const relations = useMemo(() => buildRelationTasks(task), [task]);
  const layout = useMemo(() => buildRelationsLayout(relations, isCreateMode), [relations, isCreateMode]);

  const selectedTitle = relations.find((relation) => relation.isSelected)?.title ?? '';

  const handleNodeClick = (workTaskId: number) => {
    task?.id && releaseWorkTaskLock(parseInt(task.id));
    navigateToTask(workTaskId, BaseWorkTaskTypeEnum.WorkTask);
  };

  const handlePlaceholderClick = (side: RelationSide) => {
    // Only a preparation task needs a day window up front. A task in the same work order inherits
    // the parent's dates and booking, and a follow up gets its dates in the create task dialog.
    if (side === 'preparation') {
      setPendingSide(side);
      return;
    }

    onCreateRelatedTask?.({ parentRelationshipType: RELATION_TYPE_BY_SIDE[side] });
  };

  const handleConfirmCreate = (minDays: number, maxDays: number) => {
    if (!pendingSide) return;

    onCreateRelatedTask?.({
      parentRelationshipType: RELATION_TYPE_BY_SIDE[pendingSide],
      minDaysFromMainTask: minDays,
      maxDaysFromMainTask: maxDays
    });
    setPendingSide(undefined);
  };

  return (
    <>
      <SectionHeader>Opgaveflow - Relationer</SectionHeader>
      <Container data-testid="task-relations-diagram">
        <DiagramArea>
          <svg
            width={layout.width}
            height={layout.height}
            viewBox={`0 0 ${layout.width} ${layout.height}`}
            role="img"
            aria-label="Opgaverelationer"
          >
            <defs>
              {layout.connectors.map((connector) => (
                <ConnectorArrowMarker
                  key={connector.key}
                  id={`relation-arrow-${connector.key}`}
                  markerWidth={8}
                  markerHeight={8}
                  refX={7}
                  refY={4}
                  orient="auto-start-reverse"
                  isPlaceholder={connector.isPlaceholder}
                >
                  <path d="M0,0 L8,4 L0,8 Z" />
                </ConnectorArrowMarker>
              ))}
            </defs>

            {layout.headers.map((header) => (
              <ColumnHeader key={header.key} x={header.x} y={header.y} textAnchor="middle">
                {header.label}
              </ColumnHeader>
            ))}

            {layout.clusters
              .filter((cluster) => !!cluster.label)
              .map((cluster) => (
                <g key={cluster.key}>
                  <ClusterBox x={cluster.x} y={cluster.y} width={cluster.width} height={cluster.height} rx={12} ry={12} />
                  <ClusterLabel x={cluster.x + 14} y={cluster.y + 18}>
                    {cluster.label}
                  </ClusterLabel>
                </g>
              ))}

            {layout.connectors.map((connector) => (
              <g key={connector.key}>
                <ConnectorLine d={connector.path} isPlaceholder={connector.isPlaceholder} />
                {(connector.label || connector.typeLabel) && (
                  <>
                    <ConnectorLabelBackground
                      x={connector.labelX - connectorLabelWidth(connector.label, connector.typeLabel) / 2}
                      y={connector.labelY - (connector.label && connector.typeLabel ? 16 : 10)}
                      width={connectorLabelWidth(connector.label, connector.typeLabel)}
                      height={connector.label && connector.typeLabel ? 32 : 20}
                      rx={10}
                      ry={10}
                    />
                    {connector.typeLabel && (
                      <ConnectorTypeLabel
                        x={connector.labelX}
                        y={connector.labelY + (connector.label ? -3 : 4)}
                        textAnchor="middle"
                      >
                        {connector.typeLabel}
                      </ConnectorTypeLabel>
                    )}
                    {connector.label && (
                      <ConnectorLabel
                        x={connector.labelX}
                        y={connector.labelY + (connector.typeLabel ? 11 : 4)}
                        textAnchor="middle"
                      >
                        {connector.label}
                      </ConnectorLabel>
                    )}
                  </>
                )}
                {/* Drawn on top of the label so a label sitting close to the target never hides the arrowhead. */}
                <ConnectorArrowOverlay
                  d={connector.path}
                  isPlaceholder={connector.isPlaceholder}
                  markerEnd={connector.arrowAtEnd ? `url(#relation-arrow-${connector.key})` : undefined}
                  markerStart={connector.arrowAtEnd ? undefined : `url(#relation-arrow-${connector.key})`}
                />
              </g>
            ))}

            {layout.nodes.map((node) => (
              <RelationNode key={node.task.id} node={node} onClick={handleNodeClick} />
            ))}

            {layout.placeholders.map((placeholder) => (
              <PlaceholderGroup
                key={placeholder.key}
                onClick={() => handlePlaceholderClick(placeholder.side)}
                data-testid={`relation-placeholder-${placeholder.side}`}
              >
                <PlaceholderBox
                  x={placeholder.x}
                  y={placeholder.y}
                  width={placeholder.width}
                  height={placeholder.height}
                  rx={10}
                  ry={10}
                />
                <PlaceholderLabel
                  x={placeholder.x + placeholder.width / 2}
                  y={placeholder.y + placeholder.height / 2 + 4}
                  textAnchor="middle"
                >
                  + Ny opgave
                </PlaceholderLabel>
              </PlaceholderGroup>
            ))}
          </svg>
        </DiagramArea>

        <FooterRow>
          {onCreateRelatedTask && (
            <Button
              variant={isCreateMode ? 'secondary' : 'primary'}
              onClick={() => setIsCreateMode(!isCreateMode)}
              data-testid="relations-create-mode-toggle"
            >
              {isCreateMode ? 'Annuller' : 'Opret opgave'}
            </Button>
          )}
        </FooterRow>

        {pendingSide && (
          <CreateRelationDialog
            side={pendingSide}
            mainTaskTitle={selectedTitle}
            onClose={() => setPendingSide(undefined)}
            onConfirm={handleConfirmCreate}
          />
        )}
      </Container>
    </>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 12px;
  padding: 16px;
  border-radius: 12px;
  background: ${(props) => props.theme.palette.functions.action.tertiary};
`;

const DiagramArea = styled.div`
  overflow-x: auto;
  overflow-y: hidden;
`;

const FooterRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  column-gap: 12px;
`;

const ColumnHeader = styled.text`
  font-family: ${(props) => props.theme.typography.b.fontFamily};
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.08em;
  fill: ${(props) => props.theme.palette.grey.black40};
`;

const ClusterBox = styled.rect`
  fill: none;
  stroke: ${(props) => props.theme.palette.grey.black20};
  stroke-width: 1;
  stroke-dasharray: 6 4;
`;

const ClusterLabel = styled.text`
  font-family: ${(props) => props.theme.typography.b.fontFamily};
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.08em;
  fill: ${(props) => props.theme.palette.grey.black60};
`;

const ConnectorLine = styled.path<{ isPlaceholder: boolean }>`
  fill: none;
  stroke: ${(props) => (props.isPlaceholder ? props.theme.palette.grey.black10 : props.theme.palette.grey.black40)};
  stroke-width: ${(props) => (props.isPlaceholder ? 1 : 1.5)};
  stroke-dasharray: ${(props) => (props.isPlaceholder ? '4 4' : 'none')};
`;

const ConnectorLabelBackground = styled.rect`
  fill: ${(props) => props.theme.palette.background.primary};
  stroke: ${(props) => props.theme.palette.grey.black10};
  stroke-width: 1;
`;

// Carries only the arrowhead marker - invisible itself, but drawn last so the marker is never
// covered by the label background that sits on top of the visible ConnectorLine underneath it.
const ConnectorArrowOverlay = styled.path<{ isPlaceholder: boolean }>`
  fill: none;
  stroke: none;
  stroke-width: ${(props) => (props.isPlaceholder ? 1 : 1.5)};
`;

const ConnectorArrowMarker = styled.marker<{ isPlaceholder: boolean }>`
  path {
    fill: ${(props) => (props.isPlaceholder ? props.theme.palette.grey.black10 : props.theme.palette.grey.black40)};
  }
`;

const ConnectorLabel = styled.text`
  font-family: ${(props) => props.theme.typography.p.fontFamily};
  font-size: 10px;
  fill: ${(props) => props.theme.palette.grey.black60};
`;

const ConnectorTypeLabel = styled.text`
  font-family: ${(props) => props.theme.typography.b.fontFamily};
  font-size: 10px;
  font-weight: 600;
  fill: ${(props) => props.theme.palette.grey.black60};
`;

const PlaceholderGroup = styled.g`
  cursor: pointer;
  opacity: 0.6;

  &:hover {
    opacity: 1;
  }
`;

const PlaceholderBox = styled.rect`
  fill: ${(props) => props.theme.palette.background.primary};
  stroke: ${(props) => props.theme.palette.functions.action.primary};
  stroke-width: 1;
  stroke-dasharray: 5 4;
`;

const PlaceholderLabel = styled.text`
  font-family: ${(props) => props.theme.typography.b.fontFamily};
  font-size: 12px;
  font-weight: 600;
  fill: ${(props) => props.theme.palette.functions.action.primary};
`;

export default TaskRelationsDiagram;
